import React from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Flex, Heading, Text, Button, Card, CardContent, LoadingOrbit, Buildings, Warning, CheckCircle } from '@chatsdet/ui';
import { API_URL } from '../../config.js';
import { useAuth } from '../../contexts/AuthContext';
import { storeInviteToken, clearInviteToken } from '../../hooks/useInviteToken';

type InviteValidation = {
  ok: boolean;
  organization?: {
    id: string;
    name: string;
  };
  error?: string;
};

export function InviteAccept() {
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const { user, isLoading: userLoading } = useAuth();

  // Persist invite token so it survives navigation to login/signup pages
  // Done synchronously (not in useEffect) so it runs before any redirect
  if (token) storeInviteToken(token);

  // Validate the invite token
  const inviteQuery = useQuery({
    queryKey: ['invite', token],
    queryFn: async (): Promise<InviteValidation> => {
      const res = await fetch(`${API_URL}/invites/${token}/validate`, {
        credentials: 'include',
      });
      return res.json();
    },
    retry: false,
  });

  // Accept the invite
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/invites/${token}/accept`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: () => {
      clearInviteToken();
      window.location.href = '/';
    },
  });

  if (inviteQuery.isLoading || userLoading) {
    return (
      <Flex align="center" justify="center" className="min-h-screen">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  // Invalid or expired invite
  if (!inviteQuery.data?.ok) {
    // Check if user has no org yet
    const userHasNoOrg = user && !user.orgId;

    return (
      <Flex align="center" justify="center" className="min-h-screen bg-background">
        <Card padding="lg" className="max-w-md w-full mx-4">
          <CardContent>
            <Flex direction="column" align="center" gap="lg" className="text-center">
              <div className="p-3 bg-warning/10 rounded-full">
                <Warning size={48} className="text-warning" />
              </div>
              <div>
                <Heading level={2}>
                  {userHasNoOrg ? 'Invitation Expired' : 'Invalid Invitation'}
                </Heading>
                <Text size="sm" className="text-muted-foreground mt-2">
                  {userHasNoOrg
                    ? 'This invitation has expired or is no longer valid. You can create your own organization to get started.'
                    : 'This invitation link is invalid or has expired.'}
                </Text>
              </div>
              {userHasNoOrg ? (
                <Flex direction="column" gap="sm" className="w-full">
                  <Button variant="primary" onPress={() => navigate({ to: '/onboarding' })}>
                    Create Your Own Organization
                  </Button>
                  <Button variant="outline" onPress={() => (window.location.href = '/')}>
                    Go to Home
                  </Button>
                </Flex>
              ) : (
                <Button variant="primary" onPress={() => (window.location.href = '/')}>
                  Go to Home
                </Button>
              )}
            </Flex>
          </CardContent>
        </Card>
      </Flex>
    );
  }

  // User not authenticated - show auth options
  if (!user) {
    return (
      <Flex align="center" justify="center" className="min-h-screen bg-background">
        <Card padding="lg" className="max-w-md w-full mx-4">
          <CardContent>
            <Flex direction="column" align="center" gap="lg" className="text-center">
              <div className="p-3 bg-primary/10 rounded-full">
                <Buildings size={48} className="text-primary" />
              </div>
              <div>
                <Heading level={2}>Join {inviteQuery.data.organization?.name}</Heading>
                <Text size="sm" className="text-muted-foreground mt-2">
                  You've been invited to join this organization. Sign in or create an account to
                  continue.
                </Text>
              </div>
              <Flex direction="column" gap="sm" className="w-full">
                <Button
                  variant="primary"
                  onPress={() => {
                    window.location.href = `${API_URL}/auth/google?invite=${token}`;
                  }}
                  className="w-full"
                >
                  Continue with Google
                </Button>
                <Button
                  variant="outline"
                  onPress={() => {
                    navigate({ to: '/signup', search: { invite: token } });
                  }}
                  className="w-full"
                >
                  Sign up with Email
                </Button>
                <Text size="sm" className="text-muted-foreground mt-2">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/login', search: { invite: token } })}
                    className="text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </Text>
              </Flex>
            </Flex>
          </CardContent>
        </Card>
      </Flex>
    );
  }

  // User is authenticated - show accept button
  return (
    <Flex align="center" justify="center" className="min-h-screen bg-background">
      <Card padding="lg" className="max-w-md w-full mx-4">
        <CardContent>
          <Flex direction="column" align="center" gap="lg" className="text-center">
            <div className="p-3 bg-primary/10 rounded-full">
              <Buildings size={48} className="text-primary" />
            </div>
            <div>
              <Heading level={2}>Join {inviteQuery.data.organization?.name}</Heading>
              <Text size="sm" className="text-muted-foreground mt-2">
                You've been invited to join this organization.
              </Text>
            </div>
            {acceptMutation.isSuccess ? (
              <Flex direction="column" align="center" gap="md">
                <div className="p-3 bg-success/10 rounded-full">
                  <CheckCircle size={48} className="text-success" />
                </div>
                <Text>Invitation accepted! Redirecting...</Text>
              </Flex>
            ) : (
              <Flex direction="column" gap="sm" className="w-full">
                <Button
                  variant="primary"
                  onPress={() => acceptMutation.mutate()}
                  isDisabled={acceptMutation.isPending}
                  className="w-full"
                >
                  {acceptMutation.isPending ? 'Accepting...' : 'Accept Invitation'}
                </Button>
                <Button
                  variant="outline"
                  onPress={() => (window.location.href = '/')}
                  isDisabled={acceptMutation.isPending}
                  className="w-full"
                >
                  Cancel
                </Button>
              </Flex>
            )}
            {acceptMutation.isError && (
              <Text size="sm" className="text-destructive">
                {acceptMutation.error instanceof Error
                  ? acceptMutation.error.message
                  : 'Failed to accept invitation'}
              </Text>
            )}
          </Flex>
        </CardContent>
      </Card>
    </Flex>
  );
}
